import { useState } from 'react';

import { useDecideAction } from '../../api/hooks';
import { ACTION_LABELS } from '../../api/types';
import type { Action } from '../../api/types';
import { Role } from '../../auth/roles';
import { usePrincipal } from '../../auth/usePrincipal';
import { Button } from '../../components/Button';
import { Callout } from '../../components/Callout';
import { ActionStatusBadge, ModeBadge } from '../../components/DomainBadges';
import { useFormatters } from '../../time/format';

/**
 * Why an approval control might be absent.
 *
 * The server is the authority — `assert_distinct_approver` enforces all of this and will
 * reject a request that gets here anyway. What this does is explain the absence, because a
 * responder who can see a pending action and no button needs to know whether that is a bug or
 * the separation-of-duties rule working. Each reason maps to a check in `api/auth.py`.
 */
function approvalBlockReason(
  action: Action,
  principal: { role: number; subject: string; upn: string },
  caseSubjects: readonly string[],
): string | null {
  if (principal.role < Role.RESPONDER) {
    return 'Approving containment requires the IRA.Responder role.';
  }
  if (action.requested_by && action.requested_by === principal.subject) {
    return 'You requested this action. A second responder must approve it — otherwise the approval gate is decorative.';
  }
  // `correlation_keys` are `kind:value` pairs, so the UPN is compared against the value half.
  const upn = principal.upn.trim().toLowerCase();
  if (upn) {
    const isSubject = caseSubjects.some((key) => {
      const separator = key.indexOf(':');
      const value = separator === -1 ? key : key.slice(separator + 1);
      return value.trim().toLowerCase() === upn;
    });
    if (isSubject) {
      return 'You are a subject of this case and cannot approve action against your own account. Escalate to another responder.';
    }
  }
  return null;
}

export function ActionRow({
  action,
  caseId,
  caseSubjects,
}: {
  action: Action;
  caseId: string;
  caseSubjects: readonly string[];
}) {
  const principal = usePrincipal();
  const decide = useDecideAction(caseId);
  const { precise, relative } = useFormatters();
  const [note, setNote] = useState('');

  const pending = action.status === 'pending_approval';
  const blockReason = approvalBlockReason(action, principal, caseSubjects);

  return (
    <li className="py-4">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-body">
          {ACTION_LABELS[action.kind] ?? action.kind}
        </span>
        <ActionStatusBadge status={action.status} />
        <ModeBadge mode={action.mode} />
        <span className="ml-auto font-mono text-[11px] text-subtle">{action.action_id}</span>
      </div>

      <p className="text-sm text-muted">
        Target: <span className="font-mono text-body">{action.target}</span>
      </p>

      {action.rationale ? (
        <p className="mt-1 text-sm text-muted">{action.rationale}</p>
      ) : null}

      {Object.keys(action.params).length > 0 ? (
        <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
          {Object.entries(action.params).map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-subtle">{key}</dt>
              <dd className="break-all font-mono text-muted">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/*
        A blocked action is a record, not a failure to hide: a policy guard refused it, and
        which guard it was is exactly what an approver needs before deciding whether to
        re-request it.
      */}
      {action.blocked_reason ? (
        <div className="mt-2">
          <Callout tone="danger">
            <p className="font-medium">Blocked by policy</p>
            <p className="mt-0.5 opacity-90">{action.blocked_reason}</p>
          </Callout>
        </div>
      ) : null}

      {action.status === 'recommended' && action.mode === 'disabled' ? (
        <p className="mt-2 text-xs text-subtle">
          Policy has this action disabled in <code className="font-mono">cdk.json</code>. It is
          recorded for the audit trail and was never executed.
        </p>
      ) : null}

      {action.result ? (
        <p className="mt-2 text-xs text-muted">
          Result: <span className="font-mono">{action.result}</span>
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] tabular-nums text-subtle">
        <span>Created {precise(action.created_at)}</span>
        {action.executed_at ? <span>Executed {precise(action.executed_at)}</span> : null}
        {action.expires_at && pending ? (
          <span className="text-warning-fg">
            Expires {relative(action.expires_at)} · {precise(action.expires_at)}
          </span>
        ) : null}
        {action.requested_by ? <span>Requested by {action.requested_by}</span> : null}
        {action.approved_by ? <span>Approved by {action.approved_by}</span> : null}
      </div>

      {pending ? (
        <div className="mt-3 rounded-lg border border-line bg-sunken p-3">
          {blockReason ? (
            <p className="text-xs text-subtle">{blockReason}</p>
          ) : (
            <>
              <label
                htmlFor={`note-${action.action_id}`}
                className="mb-1 block text-xs font-medium text-muted"
              >
                Note for the audit trail (optional)
              </label>
              <input
                id={`note-${action.action_id}`}
                type="text"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why you are approving or rejecting"
                className="mb-2 w-full rounded-lg border border-line-strong bg-surface px-3 py-1.5 text-sm text-body placeholder:text-subtle"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="danger"
                  size="xs"
                  disabled={decide.isPending}
                  onClick={() =>
                    decide.mutate({ actionId: action.action_id, decision: 'approve', note })
                  }
                >
                  Approve containment
                </Button>
                <Button
                  variant="secondary"
                  size="xs"
                  disabled={decide.isPending}
                  onClick={() =>
                    decide.mutate({ actionId: action.action_id, decision: 'reject', note })
                  }
                >
                  Reject
                </Button>
                {decide.isPending ? (
                  <span className="text-xs text-subtle">Submitting…</span>
                ) : null}
              </div>
            </>
          )}

          {/*
            A refusal from the server is the expected path here, not an anomaly — its message
            names which rule refused, so it is shown verbatim.
          */}
          {decide.error ? (
            <div className="mt-2">
              <Callout tone="warning" role="alert">
                {decide.error.message}
              </Callout>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
